// Notices that a newer screen is out and gets this window onto it.
//
// The screen's JS and CSS are served immutable and index.html no-cache, so a
// window that stays open (the mac desktop, a home screen app, a pinned tab)
// keeps running the build it loaded until it loads the page again. Each build
// writes its id to /version.json (vite.config.ts); this module reads it when
// the window comes back or goes away (visibility, focus, back online) — never
// on a timer — and compares it with the id built into this bundle.
//
// A newer build:
// - while the window is shown, a button offers to load it (AppUpdateBar). It
//   never reloads by itself: the person may be reading or writing.
// - while the window is hidden, it loads it then and there, unless a call is
//   on (reloading ends it) or the focused field holds text (it may not be kept
//   as a draft).
//
// A window that stays shown across a deploy still reaches a removed split JS
// file when it moves: stale-chunk.ts reloads once for that.

import { useSyncExternalStore } from "react";

export interface AppUpdateDeps {
  /** The id built into this bundle; "" or "dev" turns this off. */
  loaded: string;
  /** Reads the id the server serves now (null when it cannot be read). */
  fetchServed?: () => Promise<string | null>;
  reload?: () => void;
  /** False while reloading would lose something (a call). */
  canReloadSilently?: () => boolean;
  now?: () => number;
}

export interface AppUpdate {
  /** True once a newer build is served; stays until the page is loaded again. */
  available(): boolean;
  subscribe(cb: () => void): () => void;
  /** Asks the server now (throttled). */
  check(): Promise<void>;
  /** Loads the new build (the button). */
  reload(): void;
  /** Starts listening to the window; returns stop. */
  install(): () => void;
}

/** Checks closer together than this are skipped (focus and visibility come in pairs). */
const MIN_INTERVAL_MS = 30_000;

async function fetchServedId(): Promise<string | null> {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    const body = (await res.json()) as { buildId?: unknown };
    return typeof body.buildId === "string" && body.buildId
      ? body.buildId
      : null;
  } catch {
    return null;
  }
}

/** A text field with something typed into it holds focus. */
function focusedFieldHasText(): boolean {
  const el = document.activeElement;
  if (el instanceof HTMLTextAreaElement) return el.value.trim() !== "";
  if (el instanceof HTMLInputElement)
    return (
      !["button", "checkbox", "radio", "submit", "range", "color"].includes(
        el.type,
      ) && el.value.trim() !== ""
    );
  if (el instanceof HTMLElement && el.isContentEditable)
    return (el.textContent ?? "").trim() !== "";
  return false;
}

export function createAppUpdate(deps: AppUpdateDeps): AppUpdate {
  const fetchServed = deps.fetchServed ?? fetchServedId;
  const doReload = deps.reload ?? (() => location.reload());
  const canReload = deps.canReloadSilently ?? (() => true);
  const now = deps.now ?? Date.now;
  const enabled = deps.loaded !== "" && deps.loaded !== "dev";

  let available = false;
  let lastCheck = Number.NEGATIVE_INFINITY;
  let inflight: Promise<void> | null = null;
  const listeners = new Set<() => void>();

  const reloadIfHidden = () => {
    if (!available || !document.hidden) return;
    if (!canReload() || focusedFieldHasText()) return;
    doReload();
  };

  const check = (): Promise<void> => {
    if (!enabled || available) return Promise.resolve();
    if (inflight) return inflight;
    if (now() - lastCheck < MIN_INTERVAL_MS) return Promise.resolve();
    lastCheck = now();
    inflight = fetchServed()
      .then((served) => {
        if (served === null || served === deps.loaded) return;
        available = true;
        for (const l of listeners) l();
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  };

  const install = () => {
    if (!enabled) return () => {};
    const onVisibility = () => {
      // Going away: ask, and load it while nobody looks. Coming back: ask.
      void check().then(reloadIfHidden);
    };
    const onCheck = () => void check();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onCheck);
    window.addEventListener("online", onCheck);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onCheck);
      window.removeEventListener("online", onCheck);
    };
  };

  return {
    available: () => available,
    subscribe(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    check,
    reload: doReload,
    install,
  };
}

export function useAppUpdateAvailable(update: AppUpdate): boolean {
  return useSyncExternalStore(
    update.subscribe,
    update.available,
    update.available,
  );
}
